/**
 * Urava analytics tracking SDK.
 *
 * Captures page views, dwell time, clicks, sessions, device info, UTM/referrer.
 * Batches events and flushes every 5s or when the tab is hidden / closed.
 *
 * Honors:
 *   - navigator.doNotTrack
 *   - localStorage `analytics_optout` = "1"
 *   - import.meta.env.VITE_ANALYTICS_DISABLED = "1"
 *
 * Wire-in:
 *   import { initAnalytics, trackPageView } from '@/utils/analytics';
 *   initAnalytics();                  // once at app boot
 *   trackPageView(location.pathname); // on every route change
 */

const ENDPOINT_BATCH = '/analytics/events';
const FLUSH_INTERVAL_MS = 5000;
const SESSION_IDLE_MS = 30 * 60 * 1000;
const ANON_KEY = 'urava_anon_id';
const SESSION_KEY = 'urava_session_id';
const SESSION_LAST_KEY = 'urava_session_last_ts';

type EventType =
  | 'page_view'
  | 'page_exit'
  | 'click'
  | 'session_start'
  | 'session_end'
  | 'custom';

interface AnalyticsEvent {
  anon_id: string;
  session_id: string;
  event_type: EventType;
  event_name?: string;
  page_path: string;
  page_title?: string;
  page_url?: string;
  element_tag?: string;
  element_id?: string;
  element_text?: string;
  element_href?: string;
  element_track?: string;
  dwell_ms?: number;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  viewport_w?: number;
  viewport_h?: number;
  occurred_at?: string;
  payload?: Record<string, unknown>;
}

let queue: AnalyticsEvent[] = [];
let flushTimer: number | null = null;
let currentPath: string | null = null;
let currentPathEnteredAt = 0;
let initialized = false;
let baseUrl = '/api/v1';

// ── Identity ────────────────────────────────────────────────────────────────

function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getAnonId(): string {
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
}

function getSessionId(): string {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(SESSION_LAST_KEY) || '0');
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid || now - last > SESSION_IDLE_MS) {
    sid = uuid();
    sessionStorage.setItem(SESSION_KEY, sid);
    sessionStorage.setItem(SESSION_LAST_KEY, String(now));
    enqueue(buildEvent('session_start'));
    return sid;
  }
  sessionStorage.setItem(SESSION_LAST_KEY, String(now));
  return sid;
}

// ── Device / UTM detection ──────────────────────────────────────────────────

function detectDevice(): { device_type: string; browser: string; os: string } {
  const ua = navigator.userAgent;
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
  const isTablet = /iPad|Tablet/i.test(ua) || (isMobile && Math.min(screen.width, screen.height) >= 600);
  const device_type = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

  let browser = 'unknown';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';

  let os = 'unknown';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return { device_type, browser, os };
}

function getUtm(): Pick<AnalyticsEvent, 'utm_source' | 'utm_medium' | 'utm_campaign'> {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source') || undefined,
    utm_medium: p.get('utm_medium') || undefined,
    utm_campaign: p.get('utm_campaign') || undefined,
  };
}

function buildEvent(event_type: EventType, extra: Partial<AnalyticsEvent> = {}): AnalyticsEvent {
  const dev = detectDevice();
  return {
    anon_id: getAnonId(),
    session_id: sessionStorage.getItem(SESSION_KEY) || uuid(),
    event_type,
    page_path: window.location.pathname,
    page_title: document.title,
    page_url: window.location.href,
    referrer: document.referrer || undefined,
    ...getUtm(),
    device_type: dev.device_type,
    browser: dev.browser,
    os: dev.os,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    occurred_at: new Date().toISOString(),
    ...extra,
  };
}

// ── Queue & flush ───────────────────────────────────────────────────────────

function isOptedOut(): boolean {
  if (typeof navigator !== 'undefined' && (navigator as any).doNotTrack === '1') return true;
  if (typeof localStorage !== 'undefined' && localStorage.getItem('analytics_optout') === '1') return true;
  if ((import.meta as any).env?.VITE_ANALYTICS_DISABLED === '1') return true;
  return false;
}

function enqueue(ev: AnalyticsEvent) {
  if (isOptedOut()) return;
  queue.push(ev);
  if (queue.length >= 25) flush();
  else scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function flush() {
  if (!queue.length) return;
  const events = queue;
  queue = [];
  const url = `${baseUrl}${ENDPOINT_BATCH}`;
  const body = JSON.stringify({ events });
  const token = localStorage.getItem('token');
  // Use fetch (keepalive=true) so we can attach auth + survive page transitions.
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    }).catch(() => {
      // Re-queue on network error so we retry on next flush
      queue.unshift(...events);
    });
  } catch {
    queue.unshift(...events);
  }
}

function flushBeacon() {
  // Used in pagehide/beforeunload — sendBeacon is fire-and-forget,
  // can't attach auth headers, but never blocks unload.
  if (!queue.length) return;
  const url = `${baseUrl}${ENDPOINT_BATCH}`;
  const blob = new Blob([JSON.stringify({ events: queue })], { type: 'application/json' });
  if (navigator.sendBeacon?.(url, blob)) {
    queue = [];
  } else {
    flush();
  }
}

// ── Public tracking surface ─────────────────────────────────────────────────

export function trackPageView(path?: string) {
  if (isOptedOut()) return;
  // Close out the previous page with a page_exit event carrying dwell_ms
  if (currentPath && currentPath !== path) {
    enqueue(
      buildEvent('page_exit', {
        page_path: currentPath,
        dwell_ms: Date.now() - currentPathEnteredAt,
      })
    );
  }
  currentPath = path ?? window.location.pathname;
  currentPathEnteredAt = Date.now();
  getSessionId(); // refresh session activity timestamp / start a new session if idle
  enqueue(buildEvent('page_view'));
}

export function trackClick(el: Element) {
  if (isOptedOut()) return;
  const tag = el.tagName.toLowerCase();
  const text = (el.textContent || '').trim().slice(0, 120);
  const href = (el as HTMLAnchorElement).href || undefined;
  const id = el.id || undefined;
  const track = el.getAttribute('data-track') || undefined;
  enqueue(
    buildEvent('click', {
      element_tag: tag,
      element_id: id,
      element_text: text || undefined,
      element_href: href,
      element_track: track,
    })
  );
}

export function trackEvent(name: string, payload?: Record<string, unknown>) {
  if (isOptedOut()) return;
  enqueue(buildEvent('custom', { event_name: name, payload }));
}

// ── Init ────────────────────────────────────────────────────────────────────

interface InitOpts {
  apiBaseUrl?: string; // default: VITE_API_URL or /api/v1
}

export function initAnalytics(opts: InitOpts = {}) {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  baseUrl = opts.apiBaseUrl || (import.meta as any).env?.VITE_API_URL || '/api/v1';

  if (isOptedOut()) return;

  // Delegated click listener — captures every click anywhere on the page
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as Element | null;
      if (!target) return;
      // Walk up to find the nearest interactive ancestor
      const el = target.closest('a, button, [role="button"], [data-track]') as Element | null;
      if (!el) return;
      trackClick(el);
    },
    { capture: true, passive: true }
  );

  // First page view + session start
  trackPageView(window.location.pathname);

  // Periodic flush
  window.setInterval(flush, FLUSH_INTERVAL_MS);

  // Tab hidden -> flush via beacon (covers tab close, navigation to other site)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (currentPath) {
        enqueue(
          buildEvent('page_exit', {
            page_path: currentPath,
            dwell_ms: Date.now() - currentPathEnteredAt,
          })
        );
      }
      flushBeacon();
    }
  });

  window.addEventListener('pagehide', () => {
    if (currentPath) {
      enqueue(
        buildEvent('page_exit', {
          page_path: currentPath,
          dwell_ms: Date.now() - currentPathEnteredAt,
        })
      );
    }
    enqueue(buildEvent('session_end'));
    flushBeacon();
  });
}

export function setAnalyticsOptOut(opt: boolean) {
  if (opt) localStorage.setItem('analytics_optout', '1');
  else localStorage.removeItem('analytics_optout');
}
